/**
 * How much of an order is actually covered, and by what.
 *
 * An order is satisfied by three different things and they are not
 * interchangeable:
 *
 *   produced — cable that exists, made for this order
 *   stock    — finished cable reserved off the shelf; it needs no production
 *   allocated— a promise from a product still being made
 *
 * Four screens worked this out for themselves and only two remembered the
 * middle one. An order filled entirely from stock therefore read "Not allocated,
 * 0%" for ever on the orders list and the sales dashboard — nothing had been
 * allocated, which was true, and nothing needed to be, which was the point.
 *
 * So the rule lives here, once. `stockReservedLength` is a first-class
 * fulfilment path, not a footnote.
 */

const num = v => (Number.isFinite(Number(v)) ? Number(v) : 0);
const arr = v => (Array.isArray(v) ? v : []);

const isCancelled = line =>
  String(line?.status || '').toUpperCase() === 'CANCELLED';

/**
 * The rule itself, over four already-summed numbers.
 *
 * Split out from `coverageOf` because analytics reads orders that no longer
 * have lines to sum — an archived order arrives as totals from the backend, and
 * a deleted one from a third collection again. They must land in the same
 * vocabulary as the live list or the two screens disagree about the same order.
 *
 * @returns {{
 *   ordered, produced, stock, allocated, covered, remaining, pct,
 *   state, fromStockOnly
 * }}
 */
export function coverageFrom({ ordered, produced, stock, allocated } = {}) {
  ordered = num(ordered);
  produced = num(produced);
  stock = num(stock);
  allocated = num(allocated);

  // Clamped: a line can be allocated AND have stock against it, and the two
  // together must never claim more of the order than the order asks for.
  const covered = Math.min(ordered, produced + stock + allocated);
  const remaining = Math.max(0, ordered - covered);
  const pct = ordered > 0 ? Math.min(100, Math.round((covered / ordered) * 100)) : 0;

  // Nothing left to make: the shelf already had it. Worth its own name because
  // it is the one state where an order is finished without production ever
  // touching it.
  const fromStockOnly =
    ordered > 0 && produced <= 0 && stock >= ordered;

  let state = 'not_allocated';
  if (ordered <= 0) state = 'empty';
  else if (produced >= ordered) state = 'completed';
  else if (fromStockOnly) state = 'from_stock';
  else if (produced > 0) state = 'in_progress';
  else if (allocated + stock >= ordered) state = 'fully_allocated';
  else if (allocated + stock > 0) state = 'partially_allocated';

  return {
    ordered,
    produced,
    stock,
    allocated,
    covered,
    remaining,
    pct,
    state,
    fromStockOnly
  };
}

/** The same, for a live order with its lines. Cancelled lines do not count. */
export function coverageOf(order) {
  const lines = arr(order?.lines).filter(l => !isCancelled(l));

  return coverageFrom({
    ordered: lines.reduce((s, l) => s + num(l.totalOrderedLength), 0),
    produced: lines.reduce((s, l) => s + num(l.producedLength), 0),
    stock: lines.reduce((s, l) => s + num(l.stockReservedLength), 0),
    allocated: lines.reduce((s, l) => s + num(l.allocatedLength), 0)
  });
}

/** How each state should read on screen. */
export const COVERAGE_BADGE = {
  empty: { label: 'No lines', color: 'secondary' },
  completed: { label: 'Completed', color: 'success' },
  from_stock: { label: 'From stock', color: 'success' },
  in_progress: { label: 'In progress', color: 'info' },
  fully_allocated: { label: 'Fully allocated', color: 'success' },
  partially_allocated: { label: 'Partially allocated', color: 'warning' },
  not_allocated: { label: 'Not allocated', color: 'danger' }
};
